import type { Ticket } from '../lib/types'

// The waiting per queue as a grid: a column per queue, its waiting ticket numbers
// under it, oldest first. Numbers only; a screen's tickets carry no names.

type WaitingTicket = Pick<Ticket, 'state' | 'queueId' | 'issuedAt' | 'sequence' | 'displayNumber'>

/** Each queue's waiting numbers, oldest first (issue time, then sequence). */
export function waitingNumbers(tickets: readonly WaitingTicket[]): Map<string, string[]> {
  const waiting = tickets
    .filter((t) => t.state === 'waiting' && t.queueId)
    .slice()
    .sort((a, b) => Date.parse(a.issuedAt) - Date.parse(b.issuedAt) || a.sequence - b.sequence)
  const out = new Map<string, string[]>()
  for (const t of waiting) {
    const list = out.get(t.queueId!) ?? []
    list.push(t.displayNumber)
    out.set(t.queueId!, list)
  }
  return out
}

/** A row's height at scale 1, in canvas units. */
export const GRID_ROW = 64

/** How the box divides: a header row of queue names, then as many ticket rows as fit. */
export function gridRows(height: number, scale: number = 1): { row: number; rows: number } {
  const row = Math.max(GRID_ROW * (scale > 0 ? scale : 1), 1)
  return { row, rows: Math.max(Math.floor((height - row) / row), 0) }
}

/** One column's cells: every number when they fit, else the oldest and "+N" in the last cell. */
export function gridColumn(numbers: readonly string[], rows: number): { shown: string[]; more: number } {
  if (numbers.length <= rows) return { shown: [...numbers], more: 0 }
  if (rows <= 0) return { shown: [], more: 0 }
  return { shown: numbers.slice(0, rows - 1), more: numbers.length - (rows - 1) }
}
